import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import RouteToast from './components/RouteToast';
import Header from './components/Header';
import Footer from './components/Footer';
import Announcement from './components/Announcement';
import BackToTop from './components/BackToTop';
import Spinner from './components/Spinner';
import { api, type NavPage, type SiteSettings } from './lib/api';
import { useAuth } from './lib/auth';
import { checkForNewerBuild, newerBuildWaiting } from './lib/freshness';
import Home from './pages/Home';
import ContentPage from './pages/ContentPage';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AccountPending from './pages/AccountPending';
import Register from './pages/Register';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';

/*
  The admin bundle carries the rich-text editor, which no public visitor needs.
  Loading it lazily keeps the public site's payload small.
*/
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const Restricted = lazy(() => import('./components/admin/Restricted'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const PagesList = lazy(() => import('./pages/admin/PagesList'));
const PageEditor = lazy(() => import('./pages/admin/PageEditor'));
const MediaLibrary = lazy(() => import('./pages/admin/MediaLibrary'));
const SiteSettingsPage = lazy(() => import('./pages/admin/SiteSettings'));
const Users = lazy(() => import('./pages/admin/Users'));
const Localities = lazy(() => import('./pages/admin/Localities'));
const Countries = lazy(() => import('./pages/admin/Countries'));
const WorkStatuses = lazy(() => import('./pages/admin/WorkStatuses'));
const Trainees = lazy(() => import('./pages/admin/Trainees'));
const Batches = lazy(() => import('./pages/admin/Batches'));
const TraineeEditor = lazy(() => import('./pages/admin/TraineeEditor'));
const TraineeView = lazy(() => import('./pages/admin/TraineeView'));
const TraineePhotoImport = lazy(() => import('./pages/admin/TraineePhotoImport'));
const Account = lazy(() => import('./pages/admin/Account'));

export default function App() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  /*
    A new deploy is picked up on a page change: the page just asked for is
    loaded fresh from the server instead, so it arrives as the new build. A
    check that turns one up while moving between pages reloads at once —
    the reader has only just arrived and has nothing open to lose.
  */
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (newerBuildWaiting()) {
      window.location.reload();
      return;
    }
    void checkForNewerBuild().then((newer) => {
      if (newer) window.location.reload();
    });
  }, [location.pathname]);

  // Coming back to a tab left open is the other moment worth a look. Only
  // noted here; the reload waits for the next page change.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void checkForNewerBuild();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  return (
    <>
      <RouteToast />
      <Routes>
      {/* The admin area has its own chrome — no public header or footer. */}
      <Route
        path="/admin"
        element={
          <Suspense fallback={<Spinner label="Loading admin" />}>
            <AdminLayout />
          </Suspense>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="pages" element={<PagesList />} />
        <Route path="pages/:id" element={<PageEditor />} />
        <Route path="media" element={<MediaLibrary />} />
        {/* The sidebar hides these for accounts without the area; this stops a
            typed URL getting past it. The server refuses the data regardless. */}
        <Route element={<Restricted permission="info" />}>
          <Route path="trainees" element={<Trainees />} />
          {/* "new" is a static segment, so it outranks :id. */}
          <Route path="trainees/new" element={<TraineeEditor />} />
          <Route path="trainees/photos" element={<TraineePhotoImport />} />
          <Route path="trainees/:id" element={<TraineeView />} />
          <Route path="trainees/:id/edit" element={<TraineeEditor />} />
          <Route path="batches" element={<Batches />} />
        </Route>
        <Route path="settings" element={<SiteSettingsPage />} />
        <Route element={<Restricted permission="users" />}>
          <Route path="users" element={<Users />} />
        </Route>
        <Route path="localities" element={<Localities />} />
        <Route path="countries" element={<Countries />} />
        <Route path="work-statuses" element={<WorkStatuses />} />
        <Route path="account" element={<Account />} />
      </Route>

      <Route path="/*" element={<PublicSite />} />
      </Routes>
    </>
  );
}

function PublicSite() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [pages, setPages] = useState<NavPage[]>([]);
  const [settings, setSettings] = useState<SiteSettings>({});
  const [ready, setReady] = useState(false);
  const { user } = useAuth();

  // Nav and settings are refetched when the session changes, because signing in
  // can reveal pages that were hidden to anonymous visitors.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api<{ pages: NavPage[] }>('/nav').catch(() => ({ pages: [] })),
      api<SiteSettings>('/settings').catch(() => ({}) as SiteSettings),
    ])
      .then(([nav, s]) => {
        if (cancelled) return;
        setPages(nav.pages);
        setSettings(s);
      })
      .finally(() => !cancelled && setReady(true));
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return (
    <div className="flex min-h-dvh flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-60 focus:rounded-lg focus:bg-ink-900 focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>

      <Header pages={pages} />
      {/* Homepage only — it is the homepage announcement, not a site-wide banner. */}
      {isHome && <Announcement data={settings.homepage_announcement} />}

      <main id="main" className="flex-1">
        {!ready ? (
          <Spinner />
        ) : (
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            {/* Returning from SSO: same form, name and email already verified. */}
            <Route path="/register/complete" element={<Register fromSso />} />
            <Route path="/account-pending" element={<AccountPending />} />
            <Route path="/contact-us" element={<Contact settings={settings} />} />
            {/* Requirements and Admission Process were merged into General Information. */}
            <Route path="/requirements" element={<Navigate to="/general-information" replace />} />
            <Route path="/admission-process" element={<Navigate to="/general-information" replace />} />
            <Route path="/:slug" element={<ContentPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        )}
      </main>

      <Footer pages={pages} settings={settings} />
      <BackToTop />
    </div>
  );
}
